import c from 'classnames';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useSelector } from 'react-redux';
import { getUserGiftState } from './redux/selectors';
import { BiData, GiftSelectClickBiEvent } from '@wix/da-bi/pkg/events';
import { Url } from '@wix/da-url';
import { useTranslation } from 'react-i18next';
import type { PapiUser } from '@wix/da-papi-types';
import LlamaIcon from '@wix/da-ds/pkg/Icons/Llama';
import CakeIcon from '@wix/da-ds/pkg/Icons/24x24/Cake';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import type { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { initGiftStateSuccess, pushGivePointsModal } from './redux/actions';
import { popModal, pushBadgeModal } from '../Modals/redux/actionCreators';
import { UserGiftInitStatus } from './redux/types';

import commonStyles from './common.scss';

export function useGiftCoreMenuItem(user: PapiUser): DropdownOption {
  const { t } = useTranslation();
  const { canBeGivenCore, initStatus } = useSelector(state =>
    getUserGiftState(state, user.useridUuid)
  );

  const isLoading = initStatus === UserGiftInitStatus.Loading;
  const coreLabel = canBeGivenCore ? t('gifting.giveCore') : t('gifting.hasCore');
  const giveCoreUrl = Url.giveCoreMembership(user.username);

  return {
    icon: isLoading ? <LoadingIndicator /> : <CoreSymbol size={IconSize.SMALL} />,
    iconClassName: c(commonStyles['core-icon'], isLoading && commonStyles['loading-spinner']),
    label: coreLabel,
    biData: BiData<GiftSelectClickBiEvent>({
      evid: 275,
      recipientid: user.useridUuid,
      menu_item_name: coreLabel,
      link_url: giveCoreUrl,
    }),
    onClick: () => window.location.assign(giveCoreUrl),
    disabled: !canBeGivenCore,
  };
}

export function useGiftPointsMenuItem(user: PapiUser): DropdownOption {
  const { t } = useTranslation();
  const dispatchPushGivePointsModal = useAction(pushGivePointsModal);

  const pointsLabel = t('gifting.givePoints');

  return {
    icon: <PointsIcon />,
    label: pointsLabel,
    biData: BiData<GiftSelectClickBiEvent>({
      evid: 275,
      recipientid: user.useridUuid,
      menu_item_name: pointsLabel,
    }),
    onClick: () => {
      dispatchPushGivePointsModal(user.username, pointsLabel);
    },
  };
}

export function useGiftLlamaMenuItem(user: PapiUser): DropdownOption {
  const { t } = useTranslation();
  const { canBeGivenLlama, initStatus } = useSelector(state =>
    getUserGiftState(state, user.useridUuid)
  );
  const dispatchPushBadgeModal = useAction(pushBadgeModal);
  const dispatchPopModal = useAction(popModal);
  const dispatchInitGiftStateSuccess = useAction(initGiftStateSuccess);

  const llamaLabel = canBeGivenLlama ? t('gifting.giveLlamaBadge') : t('gifting.hasGotLlamaBadge');
  const isGiftStateLoading = initStatus === UserGiftInitStatus.Loading;

  return {
    icon: isGiftStateLoading ? <LoadingIndicator /> : <LlamaIcon />,
    iconClassName: c(isGiftStateLoading && commonStyles['loading-spinner']),
    label: llamaLabel,
    biData: BiData<GiftSelectClickBiEvent>({
      evid: 275,
      recipientid: user.useridUuid,
      menu_item_name: llamaLabel,
    }),
    onClick: () => {
      dispatchPushBadgeModal(
        user.username,
        () => dispatchInitGiftStateSuccess(user, { canBeGivenLlama: false }),
        () => dispatchPopModal(),
        'llama'
      );
    },
    disabled: !canBeGivenLlama,
  };
}

export function useGiftCakeMenuItem(user: PapiUser): DropdownOption {
  const { t } = useTranslation();
  const { canBeGivenCake, initStatus } = useSelector(state =>
    getUserGiftState(state, user.useridUuid)
  );
  const dispatchPushBadgeModal = useAction(pushBadgeModal);
  const dispatchPopModal = useAction(popModal);
  const dispatchInitGiftStateSuccess = useAction(initGiftStateSuccess);

  const cakeLabel = canBeGivenCake ? t('gifting.giveCakeBadge') : t('gifting.hasGotCakeBadge');
  const isGiftStateLoading = initStatus === UserGiftInitStatus.Loading;

  return {
    icon: isGiftStateLoading ? <LoadingIndicator /> : <CakeIcon />,
    iconClassName: c(isGiftStateLoading && commonStyles['loading-spinner']),
    label: cakeLabel,
    biData: BiData<GiftSelectClickBiEvent>({
      evid: 275,
      recipientid: user.useridUuid,
      menu_item_name: cakeLabel,
    }),
    onClick: () => {
      dispatchPushBadgeModal(
        user.username,
        () => dispatchInitGiftStateSuccess(user, { canBeGivenCake: false }),
        () => dispatchPopModal(),
        'cake'
      );
    },
    disabled: !canBeGivenCake,
  };
}

export function useGiftMenuItems(user: PapiUser) {
  const giftCoreItem = useGiftCoreMenuItem(user);
  const giftPointsItem = useGiftPointsMenuItem(user);
  const giftLlamaItem = useGiftLlamaMenuItem(user);
  const giftCakeItem = useGiftCakeMenuItem(user);

  return [giftCoreItem, giftPointsItem, giftLlamaItem, giftCakeItem];
}
